/**
 * The STARHIVE marquee as numbers (docs/spec/04 §6): letters of fat pixels cut from the 8 x 8 font,
 * a cold gradient on the first half of the name and a hot one on the second, the drop-in, the shine
 * that sweeps across, and the frame of chasing bulbs. Pure.
 */

import { LETTERS } from '../../render/fontLetters';
import { SYMBOLS } from '../../render/fontSymbols';
import { BLUE, CYAN, ORANGE, RED, SKY, WHITE, YELLOW } from '../../render/palette';
import type { Rect } from './menuModel';
import { wrapIndex } from './menuModel';

/** One font pixel becomes a block this big: taller than wide, like a marquee's lettering. */
export const BLOCK_W = 3;
export const BLOCK_H = 4;
const GLYPH_COLS = 8;
const GLYPH_ROWS = 7;
export const LETTER_W = GLYPH_COLS * BLOCK_W;
export const LOGO_H = GLYPH_ROWS * BLOCK_H;

/** A run of lit font pixels on one row of a glyph. */
export interface LogoRun {
  readonly col: number;
  readonly row: number;
  readonly len: number;
}

/** A character the font lacks is a solid block, so a renamed game still gets a logo. */
const SOLID = Array.from({ length: GLYPH_ROWS }, () => '.######.').join('|');
const cache = new Map<string, readonly LogoRun[]>();

/** The horizontal runs that make up `ch`, top row first. */
export function glyphRuns(ch: string): readonly LogoRun[] {
  const key = ch.toUpperCase();
  const hit = cache.get(key);
  if (hit) return hit;
  const rows = (LETTERS[key] ?? SYMBOLS[key] ?? SOLID).split('|').slice(0, GLYPH_ROWS);
  const runs: LogoRun[] = [];
  rows.forEach((cells, row) => {
    let start = -1;
    for (let col = 0; col <= GLYPH_COLS; col++) {
      const lit = cells[col] === '#';
      if (lit && start < 0) start = col;
      if (!lit && start >= 0) {
        runs.push({ col: start, row, len: col - start });
        start = -1;
      }
    }
  });
  cache.set(key, runs);
  return runs;
}

/** The logo's width in pixels: a font cell of blocks per letter. */
export function logoWidth(text: string): number {
  return [...text].length * LETTER_W;
}

/** White at the top through `a` and `b` to `c` at the foot, one line of each mixed in where they meet. */
function ramp(a: number, b: number, c: number): readonly number[] {
  const rows = [WHITE, WHITE, a, WHITE];
  for (let i = 0; i < 6; i++) rows.push(a);
  rows.push(b, a);
  for (let i = 0; i < 6; i++) rows.push(b);
  rows.push(c, b);
  while (rows.length < LOGO_H) rows.push(c);
  return rows;
}

const COLD = ramp(CYAN, SKY, BLUE);
const HOT = ramp(YELLOW, ORANGE, RED);

/** The colours of letter `i` of `n`, one per pixel row: starlight for STAR, honey for HIVE. */
export function logoRamp(i: number, n: number): readonly number[] {
  return i < Math.ceil(n / 2) ? COLD : HOT;
}

const DROP_FROM = 96;
const FALL_MS = 360;
const BOUNCE_MS = 160;
const STAGGER_MS = 70;

/** When letter `i` starts to fall: left to right, like a sign being lit. */
export function dropStart(i: number): number {
  return i * STAGGER_MS;
}

/** Letter `i`'s vertical offset `ms` after the drop began: falling, a small bounce, then still. */
export function dropOffset(ms: number, i: number): number {
  const t = ms - dropStart(i);
  if (t < 0) return -DROP_FROM;
  if (t < FALL_MS) return Math.round(-DROP_FROM * (1 - (t / FALL_MS) ** 2));
  const b = t - FALL_MS;
  if (b < BOUNCE_MS) return -Math.round(4 * Math.sin((Math.PI * b) / BOUNCE_MS));
  return 0;
}

/** All `n` letters have landed. */
export function dropDone(ms: number, n: number): boolean {
  return ms >= dropStart(Math.max(0, n - 1)) + FALL_MS + BOUNCE_MS;
}

const SHINE_EVERY = 5200;
const SHINE_MS = 900;
const SHINE_LEAN = 0.6;
const SHINE_HALF = 3;

/** Where the shine is at `ms` across a logo `width` wide (its x at the top row), or null between sweeps. */
export function shineAt(ms: number, width: number): number | null {
  const t = ((ms % SHINE_EVERY) + SHINE_EVERY) % SHINE_EVERY;
  if (t >= SHINE_MS) return null;
  return Math.round(-SHINE_HALF + ((width + LOGO_H * SHINE_LEAN + 2 * SHINE_HALF) * t) / SHINE_MS);
}

/** The part of the span [x0, x1) on pixel row `y` that a shine at `g` lights, or null. */
export function shineSpan(g: number, y: number, x0: number, x1: number): readonly [number, number] | null {
  const centre = g - Math.round(y * SHINE_LEAN);
  const a = Math.max(x0, centre - SHINE_HALF);
  const b = Math.min(x1, centre + SHINE_HALF);
  return b > a ? [a, b] : null;
}

export interface Bulb {
  readonly x: number;
  readonly y: number;
}

/** Bulbs every `step` pixels round the edge of `r`, clockwise from its top-left corner. */
export function marqueeBulbs(r: Rect, step = 8): Bulb[] {
  const out: Bulb[] = [];
  const nx = Math.max(1, Math.round(r.w / step));
  const ny = Math.max(1, Math.round(r.h / step));
  for (let i = 0; i < nx; i++) out.push({ x: r.x + i * step, y: r.y });
  for (let i = 0; i < ny; i++) out.push({ x: r.x + nx * step, y: r.y + i * step });
  for (let i = 0; i < nx; i++) out.push({ x: r.x + (nx - i) * step, y: r.y + ny * step });
  for (let i = 0; i < ny; i++) out.push({ x: r.x, y: r.y + (ny - i) * step });
  return out;
}

/** Bulb `i` is lit on chase step `step`: one in three, running round the frame. */
export function bulbLit(i: number, step: number): boolean {
  return wrapIndex(i - step, 3) === 0;
}
